import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Send, AlertCircle, Loader2, Building2, History, Search, X, CheckSquare, Square, FlaskConical, Paperclip, FileText, Users } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useFetch, Spinner } from '../lib/useFetch';
import { mailingApi } from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const PRESET_TEMPLATES = [
  { id: 'cibo_normativa', subject: 'Nuove normative HACCP', body: '<p>Gentile {{azienda}},</p><p>Vi informiamo che è stata pubblicata una <b>nuova normativa</b> in materia di igiene alimentare che potrebbe interessare la vostra attività.</p><p>Cordiali saluti,<br>Centro Sicurezza Consulenze</p>' },
  { id: 'avviso_generico', subject: 'Comunicazione importante', body: '<p>Gentile {{azienda}},</p><p>Vi informiamo che...</p><p>Cordiali saluti.</p>' },
  { id: 'rinnovo_corsi', subject: 'Promemoria corsi in scadenza', body: '<p>Gentile {{azienda}},</p><p>Vi ricordiamo che alcuni corsi del vostro personale sono in scadenza.</p>' },
];

const EMPTY_SEGMENT = {
  macrosettori: [],
  atecoPrefixes: [],
  atecoSearch: '',
  haccpProfilato: null,
  haccpConRegistri: null,
  piscinaProfilata: null,
  piscinaConVasche: null,
  rsppCsc: null,
  cantieri: null,
  controlloVVF: null,
  over15Dipendenti: null,
  contratti: [],
  stato: [],
  profili: [],
  includeCompanyIds: [],
  excludeCompanyIds: [],
  companyIds: null,
};

const PAGE_SIZE = 25;

// Tri-state cycle: null → true → false → null
const nextTri = (v) => (v === null ? true : v === true ? false : null);
const triLabel = (v) => (v === null ? 'Qualsiasi' : v === true ? 'Sì' : 'No');
const triCls = (v) => v === true
  ? 'bg-emerald-600 text-white border-emerald-700'
  : v === false
    ? 'bg-red-600 text-white border-red-700'
    : 'bg-white text-slate-600 border-slate-300';

const TriButton = ({ label, hint, value, onChange, testId }) => (
  <button onClick={() => onChange(nextTri(value))} data-testid={testId} className={`px-2.5 py-1 rounded-full text-xs font-semibold border-2 ${triCls(value)} transition`} title={hint}>
    {label}: <b className="ml-0.5">{triLabel(value)}</b>
  </button>
);

const RunRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = (r.errors_detail?.length || 0) + (r.skipped?.length || 0) > 0;
  return (
    <div className="border border-slate-100 rounded-lg text-xs" data-testid={`run-${r.id}`}>
      <div className="flex items-center gap-3 px-3 py-2">
        <div className="w-7 h-7 rounded-lg bg-[#e0e7ff] text-[#023F80] flex items-center justify-center"><Mail size={13} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800 truncate">{r.subject}</div>
          <div className="text-[10px] text-slate-500">{new Date(r.at).toLocaleString('it-IT')} · {r.recipients} destinatari</div>
        </div>
        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">{r.sent} inviate</span>
        {r.errors > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">{r.errors} errori</span>}
        {r.skipped?.length > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">{r.skipped.length} saltate</span>}
        {hasDetails && (
          <button onClick={() => setOpen((o) => !o)} data-testid={`run-${r.id}-details`} className="text-[10px] font-bold text-[#023F80] underline">
            {open ? 'Nascondi' : 'Dettagli'}
          </button>
        )}
        {(r.attachments || []).length > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold inline-flex items-center gap-1" title={r.attachments.map((a) => a.filename).join(', ')}>
            <Paperclip size={9} />{r.attachments.length}
          </span>
        )}
      </div>
      {open && (
        <div className="px-3 pb-2 space-y-1 border-t border-slate-100 pt-2 text-[11px]">
          {(r.errors_detail || []).length > 0 && (
            <div>
              <div className="font-bold text-red-700 mb-0.5">Errori ({r.errors_detail.length})</div>
              {r.errors_detail.map((e, i) => {
                const err = e.error || (e.status ? `invio non riuscito (status=${e.status})` : 'errore sconosciuto');
                const isIpError = /unrecognised ip|unauthorised|authorised_ips|unauthorized/i.test(err);
                const ipMatch = err.match(/\b(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\b/);
                const isDedupe = /dedupe|duplicato/i.test(err);
                const isNoKey = /brevo_api_key|non configurato/i.test(err);
                return (
                  <div key={i} className="bg-red-50 border border-red-200 rounded px-2 py-1 mb-0.5">
                    <b>{e.email}</b> → <span className="font-mono text-[10px]">{err}</span>
                    {isIpError && (
                      <div className="mt-1 text-[11px] text-red-800 bg-red-100 rounded px-2 py-1">
                        <b>Come risolvere:</b> IP del server {ipMatch ? <code className="font-mono">{ipMatch[1]}</code> : null} non è autorizzato su Brevo. Aggiungilo (o disabilita il filtro IP) qui: {' '}
                        <a href="https://app.brevo.com/security/authorised_ips" target="_blank" rel="noreferrer" className="underline font-bold">app.brevo.com/security/authorised_ips</a>
                      </div>
                    )}
                    {isDedupe && (
                      <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        <b>Dedupe interno:</b> questa combinazione destinatario+oggetto è già stata inviata in questa sessione del server. Dopo il prossimo deploy / riavvio il dedupe si resetta. Modifica l'oggetto per forzare un nuovo invio.
                      </div>
                    )}
                    {isNoKey && (
                      <div className="mt-1 text-[11px] text-red-800 bg-red-100 rounded px-2 py-1">
                        <b>Chiave Brevo mancante:</b> aggiungi <code>BREVO_API_KEY</code> ai Secrets dell'ambiente di produzione e riavvia il backend.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {(r.skipped || []).length > 0 && (
            <div>
              <div className="font-bold text-amber-700 mb-0.5">Saltate ({r.skipped.length})</div>
              {r.skipped.map((s, i) => (
                <div key={i} className="bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-0.5">
                  <b>{s.name}</b> ({s.id}) — {s.reason === 'no_email' ? 'nessuna email valida' : s.reason === 'duplicate_email' ? `email duplicata (${s.email})` : s.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MailingPage = () => {
  const [segment, setSegment] = useState(EMPTY_SEGMENT);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showSummary, setShowSummary] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [testRecipientsStr, setTestRecipientsStr] = useState('');
  const [attachments, setAttachments] = useState([]); // [{filename, content_b64, size, content_type}]
  const [allowDupEmails, setAllowDupEmails] = useState(false);
  const isDup = (it) => !allowDupEmails && it.duplicateEmail;
  const [readingFiles, setReadingFiles] = useState(false);

  const MAX_TOTAL = 10 * 1024 * 1024;
  const MAX_SINGLE = 7 * 1024 * 1024;
  const attachTotal = attachments.reduce((sum, a) => sum + (a.size || 0), 0);
  const fmtKB = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

  const handleAddFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setReadingFiles(true);
    try {
      const curTotal = attachTotal;
      const next = [...attachments];
      for (const file of files) {
        if (file.size > MAX_SINGLE) {
          toast.error(`"${file.name}" supera 7 MB (dimensione: ${fmtKB(file.size)})`);
          continue;
        }
        if (curTotal + next.reduce((s, a) => s + a.size, 0) - attachTotal + file.size > MAX_TOTAL) {
          toast.error(`Aggiungendo "${file.name}" si supera il limite totale di 10 MB`);
          continue;
        }
        // Read as base64
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || '';
            const idx = String(result).indexOf('base64,');
            resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        next.push({ filename: file.name, content_b64: b64, size: file.size, content_type: file.type });
      }
      setAttachments(next);
    } catch (e) {
      toast.error('Errore durante la lettura dei file');
    } finally {
      setReadingFiles(false);
    }
  };

  const removeAttachment = (idx) => setAttachments((xs) => xs.filter((_, i) => i !== idx));

  const { data: runs, reload: reloadRuns } = useFetch(() => mailingApi.runs(20), []);
  const { data: filterOpts } = useFetch(() => mailingApi.filterOptions(), []);
  const { data: atecoOpts } = useFetch(() => mailingApi.atecoOptions(), []);

  const set = (k, v) => setSegment((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, val) => {
    const arr = segment[k] || [];
    set(k, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  // Preview reactive: cambia solo i FILTRI (non le selezioni manuali)
  const filterSignature = JSON.stringify({
    ...segment,
    includeCompanyIds: undefined,
    excludeCompanyIds: undefined,
    companyIds: undefined,
  });
  useEffect(() => {
    setPreviewing(true);
    // IMPORTANTE: non inviamo exclude/include al preview. L'utente deve
    // continuare a vedere TUTTE le aziende filtrate anche se le ha
    // temporaneamente deselezionate (altrimenti le "perde" dall'elenco).
    const seg = {
      ...segment,
      includeCompanyIds: [],
      excludeCompanyIds: [],
      companyIds: null,
    };
    mailingApi.preview(seg).then((res) => setPreview(res.data)).finally(() => setPreviewing(false));
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature]);

  // Filtered items by search
  const items = preview?.items || [];
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (
      (it.name || '').toLowerCase().includes(q)
      || (it.codiceAteco || '').toLowerCase().includes(q)
      || (it.macrosettore || '').toLowerCase().includes(q)
      || (it.chosenEmail || '').toLowerCase().includes(q)
    ));
  }, [items, search]);
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Selected set: dedotto da (items visibili) MENO excludeCompanyIds
  // Un'azienda è "selezionata" se è nei preview items e NON è in excludeCompanyIds.
  // Oppure se è stata aggiunta manualmente via includeCompanyIds.
  const excludeSet = useMemo(() => new Set(segment.excludeCompanyIds || []), [segment.excludeCompanyIds]);
  const includeSet = useMemo(() => new Set(segment.includeCompanyIds || []), [segment.includeCompanyIds]);
  const selectedItems = useMemo(() => items.filter((it) => !excludeSet.has(it.id)), [items, excludeSet]);
  const effectiveRecipients = useMemo(() => {
    if (allowDupEmails) return selectedItems.filter((it) => it.chosenEmail).length;
    const emails = new Set();
    selectedItems.forEach((it) => { if (it.chosenEmail && !it.duplicateEmail) emails.add(it.chosenEmail); });
    return emails.size;
  }, [selectedItems, allowDupEmails]);
  const skipped = useMemo(() => {
    const noEmail = selectedItems.filter((it) => !it.chosenEmail);
    const dups = selectedItems.filter((it) => it.chosenEmail && isDup(it));
    return { noEmail, dups, total: noEmail.length + dups.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, allowDupEmails]);

  const toggleRow = (id) => {
    // Se è in excludeSet, rimuovilo. Altrimenti aggiungilo.
    const curr = segment.excludeCompanyIds || [];
    if (curr.includes(id)) set('excludeCompanyIds', curr.filter((x) => x !== id));
    else set('excludeCompanyIds', [...curr, id]);
  };

  const selectAllFiltered = () => {
    // Rimuovi dall'exclude TUTTI gli id attualmente filtrati
    const filteredIds = new Set(filteredItems.map((x) => x.id));
    set('excludeCompanyIds', (segment.excludeCompanyIds || []).filter((id) => !filteredIds.has(id)));
  };

  const deselectAllFiltered = () => {
    // Aggiungi all'exclude TUTTI gli id attualmente filtrati
    const filteredIds = filteredItems.map((x) => x.id);
    const curr = segment.excludeCompanyIds || [];
    const next = Array.from(new Set([...curr, ...filteredIds]));
    set('excludeCompanyIds', next);
  };

  const zeroWorkerIds = useMemo(() => items.filter((it) => !it.workersCount).map((it) => it.id), [items]);
  const zeroDeselected = zeroWorkerIds.filter((id) => excludeSet.has(id)).length;
  const zeroAllDeselected = zeroWorkerIds.length > 0 && zeroDeselected === zeroWorkerIds.length;
  const deselectedCount = items.length - selectedItems.length;
  const toggleZeroWorkers = () => {
    const curr = segment.excludeCompanyIds || [];
    if (zeroAllDeselected) {
      const z = new Set(zeroWorkerIds);
      set('excludeCompanyIds', curr.filter((id) => !z.has(id)));
    } else {
      set('excludeCompanyIds', Array.from(new Set([...curr, ...zeroWorkerIds])));
    }
  };

  const clearAllSelections = () => {
    set('excludeCompanyIds', items.map((x) => x.id));
  };

  const resetAllFilters = () => setSegment(EMPTY_SEGMENT);

  const applyTemplate = (t) => { setSubject(t.subject); setBodyHtml(t.body); };

  const canSend = selectedItems.length > 0 && effectiveRecipients > 0 && subject.trim() && bodyHtml.trim();

  const openSummary = () => {
    if (!subject.trim()) return toast.error('Inserisci un oggetto');
    if (!bodyHtml.trim()) return toast.error('Inserisci il contenuto');
    if (effectiveRecipients === 0) return toast.error('Nessun destinatario valido nella selezione corrente');
    setShowSummary(true);
  };

  const doSend = async () => {
    setSending(true);
    try {
      // Il backend ricalcola la selezione a partire da segment.companyIds (lista esplicita)
      // → passo gli id selezionati come companyIds (override).
      const seg = { ...segment, companyIds: selectedItems.map((x) => x.id) };
      const testRecipients = testRecipientsStr.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
      const extra = testMode ? { testMode: true, testRecipients } : {};
      if (allowDupEmails) extra.allowDuplicateEmails = true;
      if (attachments.length > 0) {
        extra.attachments = attachments.map((a) => ({ filename: a.filename, content_b64: a.content_b64, size: a.size, content_type: a.content_type }));
      }
      const res = await mailingApi.send(seg, subject, bodyHtml, extra);
      const d = res.data;
      if (d.testMode) {
        toast.success(`Test inviato a ${d.test_recipients.length} indirizzi (sarebbero stati ${d.would_send_to} invii reali)`);
      } else if (d.errors > 0) {
        toast.warning(`Inviate: ${d.sent} · Errori: ${d.errors}`);
      } else {
        toast.success(`Email inviate a ${d.sent} aziende · ${d.skipped?.length || 0} saltate`);
      }
      setShowSummary(false);
      reloadRuns();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio'); }
    finally { setSending(false); }
  };

  const activeFilterCount = (
    (segment.macrosettori || []).length +
    (segment.atecoPrefixes || []).length +
    (segment.contratti || []).length +
    (segment.stato || []).length +
    (segment.profili || []).length +
    (segment.atecoSearch ? 1 : 0) +
    (segment.haccpProfilato !== null ? 1 : 0) +
    (segment.haccpConRegistri !== null ? 1 : 0) +
    (segment.piscinaProfilata !== null ? 1 : 0) +
    (segment.piscinaConVasche !== null ? 1 : 0) +
    (segment.rsppCsc !== null ? 1 : 0) +
    (segment.cantieri !== null ? 1 : 0) +
    (segment.over15Dipendenti !== null ? 1 : 0) +
    (segment.controlloVVF !== null ? 1 : 0)
  );

  return (
    <div className="space-y-5" data-testid="mailing-page">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-[#fef7d4] text-[#8a6608] flex items-center justify-center">
            <Mail size={20} />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-lg font-bold text-gray-800 leading-tight">Mailing aziende</h2>
            <p className="text-xs text-slate-500 mt-0.5">Segmenta via profilazione (HACCP, Piscina, ATECO, Contratto, Stato) e seleziona manualmente i destinatari. Tag <code>{`{{azienda}}`}</code> per personalizzare.</p>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={resetAllFilters} data-testid="mailing-reset-filters" className="text-[11px] font-bold text-slate-600 hover:text-slate-900 flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 hover:bg-slate-200">
              <X size={12} /> Azzera filtri ({activeFilterCount})
            </button>
          )}
        </div>

        {/* ============== FILTRI DALLA PROFILAZIONE ============== */}
        <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="mailing-filters">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Filtri profilazione aziende <span className="text-slate-400">(AND tra categorie · OR dentro ogni categoria)</span></h3>

          {/* Riga 1: HACCP, Piscina, servizi */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">HACCP</div>
              <div className="flex flex-wrap gap-1.5">
                <TriButton testId="flt-haccp-prof" label="Profilato" hint="Somministrazione/Supporto HACCP = SI o profilo haccp_obbligatorio" value={segment.haccpProfilato} onChange={(v) => set('haccpProfilato', v)} />
                <TriButton testId="flt-haccp-reg" label="Con registri" hint="Almeno un record nella collection HACCP" value={segment.haccpConRegistri} onChange={(v) => set('haccpConRegistri', v)} />
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">Piscina</div>
              <div className="flex flex-wrap gap-1.5">
                <TriButton testId="flt-pisc-prof" label="Profilata" hint="presenzaPiscine=SI o responsabile piscina valorizzato" value={segment.piscinaProfilata} onChange={(v) => set('piscinaProfilata', v)} />
                <TriButton testId="flt-pisc-vasc" label="Con vasche create" hint="Almeno una vasca attiva nel modulo Piscina" value={segment.piscinaConVasche} onChange={(v) => set('piscinaConVasche', v)} />
              </div>
            </div>
          </div>

          {/* Riga 2: Macrosettori + ATECO */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">Macrosettore / ATECO</div>
            <div className="grid md:grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-slate-500 mb-1">Macrosettore (da ATECO, {filterOpts?.data?.macrosettori?.length || 0})</div>
                <div className="max-h-28 overflow-y-auto border border-slate-200 rounded bg-white p-1.5 space-y-0.5" data-testid="flt-macrosettori">
                  {(filterOpts?.data?.macrosettori || []).map((m) => (
                    <label key={m} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                      <Checkbox checked={(segment.macrosettori || []).includes(m)} onCheckedChange={() => toggleArr('macrosettori', m)} />
                      <span className="truncate" title={m}>{m}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 mb-1">Prefisso ATECO (click per toggle)</div>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto" data-testid="flt-ateco-prefixes">
                  {(atecoOpts?.data?.prefixes || []).map((p) => {
                    const on = (segment.atecoPrefixes || []).includes(p.prefix);
                    return (
                      <button key={p.prefix} onClick={() => toggleArr('atecoPrefixes', p.prefix)} className={`px-2 py-0.5 rounded text-[11px] font-bold border ${on ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`} title={p.label}>
                        {p.prefix} <span className="opacity-70">({p.count})</span>
                      </button>
                    );
                  })}
                </div>
                <Input value={segment.atecoSearch || ''} onChange={(e) => set('atecoSearch', e.target.value)} placeholder="Ricerca per codice o descrizione (es. 55.10, ristora…)" data-testid="flt-ateco-search" className="h-8 mt-2 text-xs" />
              </div>
            </div>
          </div>

          {/* Riga 3: Contratto, Status, Servizi */}
          <div className="grid md:grid-cols-3 gap-3">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">Tipologia contratto</div>
              <div className="flex flex-wrap gap-1" data-testid="flt-contratti">
                {(filterOpts?.data?.contratti || []).map((c) => {
                  const on = (segment.contratti || []).includes(c);
                  return (
                    <button key={c} onClick={() => toggleArr('contratti', c)} className={`px-2 py-0.5 rounded text-[11px] font-bold border ${on ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-300'}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">Stato</div>
              <div className="text-[10px] text-slate-500 mb-1.5" data-testid="dismesse-hint">Le aziende dismesse sono sempre escluse dagli invii.</div>
              <div className="flex flex-wrap gap-1" data-testid="flt-stato">
                {['attiva'].map((s) => {
                  const on = (segment.stato || []).includes(s);
                  return (
                    <button key={s} onClick={() => toggleArr('stato', s)} className={`px-2 py-0.5 rounded text-[11px] font-bold border capitalize ${on ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-700 border-slate-300'}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] font-bold uppercase text-slate-600 mb-2">Servizi / Requisiti</div>
              <div className="flex flex-wrap gap-1">
                <TriButton testId="flt-rspp" label="RSPP CSC" hint="rspp_csc=SI" value={segment.rsppCsc} onChange={(v) => set('rsppCsc', v)} />
                <TriButton testId="flt-cantieri" label="Cantieri" hint="cantieri=SI" value={segment.cantieri} onChange={(v) => set('cantieri', v)} />
                <TriButton testId="flt-vvf" label="VVF" hint="controlloVVF" value={segment.controlloVVF} onChange={(v) => set('controlloVVF', v)} />
                <TriButton testId="flt-over15" label=">15 dip." hint="over15Dipendenti" value={segment.over15Dipendenti} onChange={(v) => set('over15Dipendenti', v)} />
              </div>
            </div>
          </div>
        </div>

        {/* ============== LISTA + SELEZIONE ============== */}
        <div className="mt-5 rounded-xl border border-slate-200 overflow-hidden" data-testid="mailing-list-panel">
          <div className="bg-slate-50 border-b border-slate-200 p-3 flex items-center gap-2 flex-wrap">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mr-2">Aziende risultanti</h3>
            {previewing ? <Loader2 size={14} className="animate-spin text-slate-500" /> : (
              <span className="text-xs text-slate-600" data-testid="preview-counts">
                <b className="text-[#023F80]">{selectedItems.length}</b> selezionate su {preview?.total || 0}
                {' · '}<b className="text-emerald-700">{effectiveRecipients}</b> destinatari unici
                {skipped.total > 0 && <> · <b className="text-amber-700">{skipped.total}</b> escluse (no email / duplicate)</>}
                {deselectedCount > 0 && <> · <b className="text-slate-700" data-testid="deselected-count">{deselectedCount}</b> deselezionate</>}
                {zeroDeselected > 0 && <> (di cui <b className="text-red-700" data-testid="zero-workers-deselected-count">{zeroDeselected}</b> con 0 dipendenti)</>}
              </span>
            )}
            <div className="ml-auto flex items-center gap-1.5 flex-wrap">
              <div className="relative">
                <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Cerca azienda…" data-testid="list-search" className="h-8 pl-7 pr-2 text-xs w-48" />
              </div>
              <button
                onClick={toggleZeroWorkers}
                disabled={!zeroWorkerIds.length}
                data-testid="btn-toggle-zero-workers"
                title="Aziende senza lavoratori attivi"
                className={`px-2 py-1 rounded text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40 ${zeroAllDeselected ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-white border border-red-300 text-red-700 hover:bg-red-50'}`}
              >
                <Users size={11} /> {zeroAllDeselected ? `Riseleziona aziende con 0 dipendenti (${zeroWorkerIds.length})` : `Deseleziona aziende con 0 dipendenti (${zeroWorkerIds.length})`}
              </button>
              <button onClick={selectAllFiltered} data-testid="btn-select-all-filtered" className="px-2 py-1 rounded text-[11px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1">
                <CheckSquare size={11} /> Seleziona tutti i filtrati
              </button>
              <button onClick={deselectAllFiltered} data-testid="btn-deselect-all-filtered" className="px-2 py-1 rounded text-[11px] font-bold bg-slate-200 text-slate-700 hover:bg-slate-300 inline-flex items-center gap-1">
                <Square size={11} /> Deseleziona i filtrati
              </button>
              <button onClick={clearAllSelections} data-testid="btn-clear-selection" className="px-2 py-1 rounded text-[11px] font-bold bg-red-100 text-red-700 hover:bg-red-200 inline-flex items-center gap-1">
                <X size={11} /> Svuota selezione
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto" data-testid="list-rows">
            {pageItems.length === 0 && !previewing && (
              <div className="p-6 text-center text-xs text-slate-500">Nessuna azienda corrisponde ai filtri correnti.</div>
            )}
            {pageItems.map((it) => {
              const selected = !excludeSet.has(it.id);
              return (
                <label key={it.id} className={`flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 text-xs cursor-pointer ${selected ? 'bg-white' : 'bg-slate-50 opacity-70'} hover:bg-slate-50`} data-testid={`row-${it.id}`}>
                  <Checkbox checked={selected} onCheckedChange={() => toggleRow(it.id)} data-testid={`chk-${it.id}`} />
                  <Building2 size={11} className="text-slate-400 shrink-0" />
                  <span className="flex-1 truncate font-semibold text-slate-800">{it.name}</span>
                  <span className={`text-[10px] font-bold px-1 rounded shrink-0 ${it.workersCount ? 'text-slate-600 bg-slate-100' : 'text-red-700 bg-red-50'}`} title="Lavoratori attivi" data-testid={`workers-count-${it.id}`}>{it.workersCount || 0} dip.</span>
                  {it.codiceAteco && <span className="text-[10px] font-mono text-slate-500 shrink-0" title={it.macrosettore}>{it.codiceAteco}</span>}
                  {it.contratto && <span className="text-[10px] font-bold text-[#023F80] bg-blue-50 px-1 rounded shrink-0">{it.contratto}</span>}
                  {it.haccpProfilato && <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1 rounded shrink-0" title={it.haccpRegistri ? 'Profilato HACCP + registri' : 'HACCP profilato'}>H{it.haccpRegistri ? '+R' : ''}</span>}
                  {it.piscinaProfilata && <span className="text-[9px] font-bold text-cyan-700 bg-cyan-100 px-1 rounded shrink-0" title={it.piscinaVasche ? 'Profilata + vasche create' : 'Piscina profilata'}>P{it.piscinaVasche ? '+V' : ''}</span>}
                  <span className={`text-[10px] shrink-0 ${it.chosenEmail ? (isDup(it) ? 'text-amber-700' : 'text-emerald-700') : 'text-red-700'}`} title={it.allEmails?.join(', ') || ''}>
                    {it.chosenEmail ? (isDup(it) ? `${it.chosenEmail} (duplicata)` : it.chosenEmail) : '— no email'}
                  </span>
                </label>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="bg-slate-50 border-t border-slate-200 p-2 flex items-center justify-between text-xs">
              <span className="text-slate-500">Pagina {page} di {totalPages} · {filteredItems.length} aziende</span>
              <div className="flex gap-1">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-0.5 rounded bg-white border border-slate-300 disabled:opacity-40">← Prec</button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="list-next-page" className="px-2 py-0.5 rounded bg-white border border-slate-300 disabled:opacity-40">Succ →</button>
              </div>
            </div>
          )}
        </div>

        {/* ============== COMPOSE ============== */}
        <div className="mt-5 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Componi email</h3>
            <div className="flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase font-semibold text-slate-500 mr-1 self-center">Template:</span>
              {PRESET_TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t)} className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700">{t.subject}</button>
              ))}
            </div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto..." data-testid="mailing-subject" />
            <RichTextEditor
              value={bodyHtml}
              onChange={setBodyHtml}
              placeholder="Scrivi qui il testo. Usa i pulsanti sopra per grassetto, corsivo, link e liste. Spazi, tabulazioni e interruzioni di riga vengono preservati."
              testId="mailing-body"
            />

            {/* ────────── Allegati ────────── */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="mailing-attachments-panel">
              <div className="flex items-center gap-2 mb-2">
                <Paperclip size={14} className="text-slate-600" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Allegati</span>
                <span className="text-[10px] text-slate-500">(max 7 MB per file · 10 MB totali)</span>
                <label className="ml-auto cursor-pointer inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#023F80] hover:bg-[#1e293b] text-white text-[11px] font-bold">
                  {readingFiles ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
                  Aggiungi file
                  <input type="file" multiple onChange={(e) => { handleAddFiles(e.target.files); e.target.value = ''; }} data-testid="mailing-attachments-input" className="hidden" />
                </label>
              </div>
              {attachments.length === 0 ? (
                <p className="text-[11px] text-slate-500 italic">Nessun allegato. Verranno aggiunti alla stessa email inviata a ogni azienda.</p>
              ) : (
                <>
                  <div className="space-y-1" data-testid="mailing-attachments-list">
                    {attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded text-[11px]" data-testid={`attach-row-${i}`}>
                        <FileText size={12} className="text-slate-500 shrink-0" />
                        <span className="flex-1 truncate font-semibold">{a.filename}</span>
                        <span className="text-slate-500 shrink-0">{fmtKB(a.size)}</span>
                        <button onClick={() => removeAttachment(i)} data-testid={`attach-remove-${i}`} title="Rimuovi allegato" className="p-0.5 rounded hover:bg-red-50 text-red-600">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className={`mt-1.5 text-[10px] ${attachTotal > MAX_TOTAL ? 'text-red-700 font-bold' : 'text-slate-500'}`}>
                    Totale: {fmtKB(attachTotal)} / 10 MB · {attachments.length} file
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Modalità invio</h3>
            <div className={`rounded-xl border p-3 ${testMode ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={testMode} onCheckedChange={(v) => setTestMode(!!v)} data-testid="toggle-testmode" />
                <FlaskConical size={14} className={testMode ? 'text-amber-700' : 'text-slate-400'} />
                <span className="text-sm font-bold">Modalità TEST</span>
              </label>
              <p className="text-[11px] text-slate-600 mt-1 leading-snug">
                {testMode
                  ? 'In TEST l\'email è inviata SOLO agli indirizzi qui sotto. Le aziende reali NON ricevono nulla.'
                  : 'Modalità REALE: ogni azienda riceve una propria email (destinatario in To, nessuno vede gli altri).'}
              </p>
              {testMode && (
                <Input value={testRecipientsStr} onChange={(e) => setTestRecipientsStr(e.target.value)} data-testid="test-recipients" placeholder="email1@test.it, email2@test.it" className="mt-2 h-8 text-xs" />
              )}
            </div>
            <label className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 cursor-pointer" data-testid="toggle-allow-dup-wrap">
              <Checkbox checked={allowDupEmails} onCheckedChange={(v) => setAllowDupEmails(!!v)} data-testid="toggle-allow-dup-emails" className="mt-0.5" />
              <span>
                <span className="text-sm font-bold block">Invia anche alle aziende con la stessa email</span>
                <span className="text-[11px] text-slate-600 leading-snug block">
                  Chi gestisce più aziende riceve una email per ciascuna azienda (con il nome dell'azienda).
                  {!allowDupEmails && skipped.dups.length > 0 && <> Ora ne vengono saltate <b>{skipped.dups.length}</b>.</>}
                </span>
              </span>
            </label>
            <button onClick={openSummary} disabled={!canSend || sending} data-testid="mailing-review-btn" className="ro-hide w-full px-4 py-2.5 rounded-full bg-[#023F80] hover:bg-[#1e293b] text-white font-bold text-sm flex items-center gap-2 justify-center disabled:opacity-40">
              <Send size={14} /> Rivedi e invia · {effectiveRecipients} destinatari
            </button>
          </div>
        </div>
      </div>

      {/* ============== STORICO ============== */}
      {runs && runs.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5" data-testid="mailing-history">
          <div className="flex items-center gap-2 mb-3">
            <History size={16} className="text-slate-500" />
            <h3 className="font-bold text-gray-800 text-sm">Storico invii ({runs.length})</h3>
          </div>
          <div className="space-y-1">
            {runs.map((r) => <RunRow key={r.id} r={r} />)}
          </div>
        </div>
      )}

      {/* ============== DIALOG RIEPILOGO ============== */}
      {showSummary && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="summary-dialog">
          <div className="absolute inset-0 bg-black/60" onClick={sending ? undefined : () => setShowSummary(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-5 max-h-[90vh] overflow-y-auto space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-[#023F80]">Riepilogo invio{testMode ? ' (TEST)' : ''}</h3>
                <p className="text-xs text-slate-500 mt-0.5">Verifica destinatari e messaggio prima di confermare. {testMode && <b className="text-amber-700">In TEST l'email andrà solo ai tuoi indirizzi di test.</b>}</p>
              </div>
              <button onClick={() => setShowSummary(false)} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Selezionate" value={selectedItems.length} color="text-[#023F80]" />
              <Stat label="Destinatari effettivi" value={effectiveRecipients} color="text-emerald-700" />
              <Stat label="Senza email" value={skipped.noEmail.length} color="text-red-700" />
              <Stat label="Duplicate (dedup)" value={skipped.dups.length} color="text-amber-700" />
            </div>
            <div>
              <h4 className="text-[11px] font-bold uppercase text-slate-600 mb-1">Destinatari ({effectiveRecipients})</h4>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded">
                {selectedItems.filter((it) => it.chosenEmail && !isDup(it)).map((it) => (
                  <div key={it.id} className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-slate-100 last:border-b-0">
                    <Building2 size={10} className="text-slate-400" />
                    <span className="flex-1 truncate font-semibold">{it.name}</span>
                    <span className="text-emerald-700">{it.chosenEmail}</span>
                  </div>
                ))}
              </div>
            </div>
            {skipped.total > 0 && (
              <div>
                <h4 className="text-[11px] font-bold uppercase text-amber-700 mb-1">Escluse dall'invio ({skipped.total})</h4>
                <div className="max-h-32 overflow-y-auto border border-amber-200 rounded">
                  {skipped.noEmail.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-amber-100 last:border-b-0">
                      <AlertCircle size={10} className="text-red-600" />
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className="text-red-600 font-bold">Nessuna email</span>
                    </div>
                  ))}
                  {skipped.dups.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-amber-100 last:border-b-0">
                      <AlertCircle size={10} className="text-amber-700" />
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className="text-amber-700 font-bold">Duplicata ({it.chosenEmail})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-[11px] font-bold uppercase text-slate-600 mb-1">Oggetto</h4>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm font-semibold">{subject}</div>
            </div>
            <div>
              <h4 className="text-[11px] font-bold uppercase text-slate-600 mb-1">Anteprima corpo (nome di esempio: {selectedItems[0]?.name || '—'})</h4>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm max-h-40 overflow-y-auto" style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: (bodyHtml || '').replace(/\{\{azienda\}\}/g, selectedItems[0]?.name || '[Azienda]') }} />
            </div>
            {attachments.length > 0 && (
              <div data-testid="summary-attachments">
                <h4 className="text-[11px] font-bold uppercase text-slate-600 mb-1 flex items-center gap-1"><Paperclip size={11} /> Allegati inclusi ({attachments.length} · {fmtKB(attachTotal)})</h4>
                <div className="border border-slate-200 rounded">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 text-[11px] border-b border-slate-100 last:border-b-0">
                      <FileText size={10} className="text-slate-500" />
                      <span className="flex-1 truncate font-semibold">{a.filename}</span>
                      <span className="text-slate-500">{fmtKB(a.size)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Gli stessi file saranno allegati a <b>ogni</b> email inviata.</p>
              </div>
            )}
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2 text-[11px] text-blue-900">
              📌 L'invio avviene <b>uno a uno</b>: ogni azienda vede soltanto il proprio indirizzo nel campo To, non quelli delle altre.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowSummary(false)} disabled={sending} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
              <button onClick={doSend} disabled={sending || effectiveRecipients === 0} data-testid="confirm-send-btn" className={`px-4 py-2 rounded-lg text-white text-sm font-bold ${testMode ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#023F80] hover:bg-[#1e293b]'} disabled:opacity-50 inline-flex items-center gap-2`}>
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {testMode ? `Invia test a ${testRecipientsStr.split(/[,;\s]+/).filter(Boolean).length} indirizzi` : `Conferma invio a ${effectiveRecipients} aziende`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
    <div className="text-[10px] uppercase font-semibold text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${color}`}>{value}</div>
  </div>
);

export default MailingPage;
